/**
 * Authentication Utility Functions
 * Simplified authentication helpers for the Lead Management Dashboard
 */

import { authService } from '@services/firebase/auth';
import type { User, UserCredential } from 'firebase/auth';

/**
 * Sign in with Google SSO
 * @returns Promise with user credential
 * @throws Error if sign-in fails
 */
export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const result = await authService.signInWithGoogle();
    return result;
  } catch (error) {
    console.error('Failed to sign in with Google:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 * @returns Promise that resolves when sign-out is complete
 */
export const signOut = async (): Promise<void> => {
  try {
    await authService.signOut();
  } catch (error) {
    console.error('Failed to sign out:', error);
    throw error;
  }
};

/**
 * Get the current authenticated user
 * @returns Current user or null if not authenticated
 */
export const getCurrentUser = (): User | null => {
  return authService.getCurrentUser();
};

/**
 * Check if user is authenticated
 * @returns true if user is authenticated, false otherwise
 */
export const isAuthenticated = (): boolean => {
  return authService.getCurrentUser() !== null;
};

/**
 * Subscribe to authentication state changes
 * @param callback Function to call when auth state changes
 * @returns Unsubscribe function
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return authService.onAuthStateChange(callback);
};

/**
 * Get user display name
 * @returns User's display name or email or 'User' as fallback
 */
export const getUserDisplayName = (): string => {
  const user = getCurrentUser();
  return user?.displayName || user?.email || 'User';
};

/**
 * Get user email
 * @returns User's email or null if not authenticated
 */
export const getUserEmail = (): string | null => {
  const user = getCurrentUser();
  return user?.email || null;
};
